import { useLang } from "@/i18n";
import { useBooking, formatRange } from "@/components/booking/useBooking";
import RangeCalendar from "@/components/booking/RangeCalendar";
import GuestsPanel from "@/components/booking/GuestsPanel";

const inputCls =
    "w-full rounded-xl border border-sand-border bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 focus:border-olive focus:ring-2 focus:ring-olive/20";

export default function BookingForm({ roomName }) {
    const { lang, t } = useLang();
    const bk = useBooking();
    const hasRange = Boolean(bk.arrival && bk.departure);

    // El popover se ancla al campo (no al formulario completo) para que abra
    // justo bajo el campo y quepa en pantalla aunque el formulario sea sticky.
    const field = (label, control, testId, panel, popover) => (
        <div
            className="relative block cursor-pointer"
            data-testid={testId}
            role="button"
            tabIndex={0}
            onClick={() => bk.openPanel(panel)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && bk.openPanel(panel)}
            aria-haspopup="dialog"
            aria-expanded={bk.panel === panel}
        >
            <span className="mb-1.5 block text-xs font-medium text-ink-muted">{label}</span>
            {control}
            {bk.panel === panel && (
                <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {popover}
                </div>
            )}
        </div>
    );

    return (
        <aside
            data-testid="booking-form"
            className="rounded-3xl bg-sand p-7 shadow-[0_8px_30px_rgba(45,51,25,0.06)] md:p-8 lg:sticky lg:top-28"
        >
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-olive">Reserva tu estancia</p>
            <h3 className="mt-2 font-display text-2xl font-medium tracking-tight text-ink">{roomName}</h3>
            <form
                className="relative mt-6 space-y-4"
                action="https://rbe.zaviaerp.com/hotel/puntapalmeras"
                method="get"
                onSubmit={bk.validateSubmit}
            >
                <input type="hidden" name="zbe_hotel_id" value="puntapalmeras" />
                <input type="hidden" name="lng" value={lang} />
                <input type="hidden" name="currency" value="MXN" />
                <input type="hidden" name="zbe_arrival" value={bk.arrival} data-testid="booking-checkin-input" />
                <input type="hidden" name="zbe_departure" value={bk.departure} data-testid="booking-checkout-input" />
                <input type="hidden" name="adults" value={bk.adults} data-testid="booking-adults-select" />
                <input type="hidden" name="children" value={bk.children} data-testid="booking-children-select" />
                {field(
                    t.booking.dates,
                    <span className={`${inputCls} block truncate ${hasRange ? "text-ink" : "text-ink-muted/70"}`} data-testid="booking-dates-display">
                        {formatRange(bk.arrival, bk.departure, lang) || t.booking.addDates}
                    </span>,
                    "booking-field-dates",
                    "dates",
                    <RangeCalendar start={bk.arrival} end={bk.departure} onChange={bk.setRange} onClose={bk.closePanel} />,
                )}
                {field(
                    t.booking.guests,
                    <span className={`${inputCls} block truncate`} data-testid="booking-guests-display">
                        {t.booking.guestSummary(bk.adults, bk.children)}
                    </span>,
                    "booking-field-guests",
                    "guests",
                    <GuestsPanel adults={bk.adults} children={bk.children} onChange={bk.setGuests} onClose={bk.closePanel} />,
                )}
                <button
                    type="submit"
                    data-testid="booking-submit-button"
                    className="w-full rounded-full bg-amber py-4 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-dark hover:shadow-lg"
                >
                    {t.booking.submit}
                </button>
            </form>
        </aside>
    );
}
